import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { dependantsIndex, deviceStatic, useStaticVersion } from '../../realtime/deviceStatic';
import { useDeviceLive } from '../../realtime/hooks';
import { deviceLive } from '../../realtime/stores';
import type { DeviceStatic, Presence } from '../../realtime/types';
import { Card, ConnectivityTag, PresenceBadge, ProtocolTag } from '../../ui/components';
import { formatDateTime, formatNumber, formatRelative } from '../../ui/format';

/** Live counts re-evaluated at most this often (a concentrator coming back can change hundreds at once). */
const REFRESH_MS = 1000;

/**
 * Communication facts of a device (model, protocol, connectivity). For a device reached through an upstream device:
 * which one, its live status, and when this device itself was last heard — never inferred from the upstream device.
 */
export function CommunicationFacts({ device }: { device: DeviceStatic & { firmware?: string } }) {
  const { t } = useTranslation();
  const up = device.upstream;
  const upPresence = useDeviceLive(up?.id ?? '', (d) => d?.presence);
  return (
    <>
      <dt>{t('devices.detail.model')}</dt>
      <dd>{device.model ?? '—'}</dd>
      <dt>{t('devices.detail.protocol')}</dt>
      <dd>{device.protocol ? <ProtocolTag protocol={device.protocol} /> : '—'}</dd>
      {device.firmware !== undefined && (
        <>
          <dt>{t('devices.detail.firmware')}</dt>
          <dd className="mono">{device.firmware || '—'}</dd>
        </>
      )}
      <dt>{t('connectivity.title')}</dt>
      <dd>
        {up && deviceStatic.has(up.id) ? (
          <Link to={`/devices/${up.id}`}>
            <ConnectivityTag device={device} />
          </Link>
        ) : (
          <ConnectivityTag device={device} />
        )}
      </dd>
      {up && (
        <>
          <dt>{t('connectivity.upstreamStatus')}</dt>
          <dd>
            <PresenceBadge presence={upPresence} />
          </dd>
        </>
      )}
    </>
  );
}

/** When the device itself was last heard (for devices reached through an upstream device). */
export function LastEvidence({ id }: { id: string }) {
  const { t } = useTranslation();
  const seen = useDeviceLive(id, (d) => d?.lastSeenAt);
  return (
    <>
      <dt>{t('presence.lastEvidence')}</dt>
      <dd title={seen ? formatDateTime(seen) : undefined}>{seen ? formatRelative(seen) : '—'}</dd>
    </>
  );
}

/** Presence counts of a set of devices from the live store, refreshed at most once per second. */
export function usePresenceCounts(ids: readonly string[]) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const off = deviceLive.subscribeChanges(() => {
      timer ??= setTimeout(() => {
        timer = undefined;
        setTick((n) => n + 1);
      }, REFRESH_MS);
    });
    return () => {
      off();
      clearTimeout(timer);
    };
  }, []);
  return useMemo(() => {
    const c: Record<Presence, number> = { online: 0, offline: 0, unknown: 0 };
    for (const id of ids) c[deviceLive.get(id)?.presence ?? 'unknown']++;
    return { ...c, total: ids.length };
    // tick: live presence changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids, tick]);
}

/** Devices this device is reached through by others (e.g. the nodes behind a concentrator), with live counts. */
export function useDependants(id: string): string[] {
  const version = useStaticVersion((s) => s.version);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- version marks registry changes
  return useMemo(() => dependantsIndex().get(id) ?? [], [id, version]);
}

export function DependantsCard({ id }: { id: string }) {
  const { t } = useTranslation();
  const ids = useDependants(id);
  const counts = usePresenceCounts(ids);
  if (!ids.length) return null;
  const sorted = ids
    .map((i) => deviceStatic.get(i))
    .filter((d): d is DeviceStatic => Boolean(d))
    .sort((a, b) => a.name.localeCompare(b.name));
  return (
    <Card
      title={t('devices.detail.dependants')}
      actions={
        <span className="toolbar">
          <Link className="btn" to={`/devices?conn=${id}`}>
            {t('nav.devices')}
          </Link>
          <Link className="btn" to={`/map?focus=${id}`}>
            {t('nav.map')}
          </Link>
        </span>
      }
    >
      <p className="muted small" style={{ marginTop: 0 }}>
        {t('devices.detail.dependantsCount', {
          online: formatNumber(counts.online),
          offline: formatNumber(counts.offline),
          unknown: formatNumber(counts.unknown),
          total: formatNumber(counts.total),
        })}
      </p>
      <div className="dependants-list">
        {sorted.map((d) => (
          <DependantRow key={d.id} d={d} />
        ))}
      </div>
    </Card>
  );
}

function DependantRow({ d }: { d: DeviceStatic }) {
  const presence = useDeviceLive(d.id, (x) => x?.presence);
  const seen = useDeviceLive(d.id, (x) => x?.lastSeenAt);
  return (
    <Link to={`/devices/${d.id}`} className="dependant-row">
      <span className="mono ellipsis">{d.name}</span>
      <span className="muted small">{seen ? formatRelative(seen) : '—'}</span>
      <PresenceBadge presence={presence} />
    </Link>
  );
}
